import { Dispatch, SetStateAction, useEffect, useState } from 'react';

// Define the type for the state and the hook
type UseLocalStorageStateReturnType<T> = [T, Dispatch<SetStateAction<T>>];

// Create the hook
export const useLocalStorageState = <T>(
  key: string,
  defaultValue: T,
): UseLocalStorageStateReturnType<T> => {
  // Retrieve the initial state from localStorage
  const getInitialValue = (): T => {
    const storedValue = localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : defaultValue;
  };

  // Use state with a default value
  const [state, setState] = useState<T>(getInitialValue);

  // Update localStorage whenever the state changes
  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  // Remove the key from localStorage on page refresh
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem(key);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [key]);

  return [state, setState];
};
